import React, { useState, useCallback } from 'react';
import { styles } from '../../../utils/styles';

import { useIconUpload } from '../../../hooks/useIconUpload';
import { useDebouncedInput } from '../../../hooks/useDebouncedInput';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';

type FooterConfigType = {
    showFooter: boolean;
    showDescription: boolean;
    descriptionText: string;
    showPreviousButton: boolean;
    previousButtonText: string;
    previousButtonShowIcon: boolean;
    previousButtonIconUrl?: string;
    previousButtonIconFile?: File | null;
    previousButtonShowTooltip: boolean;
    previousButtonTooltipText?: string;
    showNextButton: boolean;
    nextButtonText: string;
    nextButtonShowIcon: boolean;
    nextButtonIconUrl?: string;
    nextButtonIconFile?: File | null;
    nextButtonShowTooltip: boolean;
    nextButtonTooltipText?: string;
    showDone: boolean;
    doneButtonText: string;
    doneButtonShowIcon: boolean;
    doneButtonIconUrl?: string;
    doneButtonIconFile?: File | null;
    doneButtonShowTooltip: boolean;
    doneButtonTooltipText?: string;
};

type FooterConfigProps = {
    id?: string;
    initialConfig?: Partial<FooterConfigType>;
    onConfigChange?: (config: FooterConfigType & {
        previousButtonIconFile?: File | null;
        nextButtonIconFile?: File | null;
        doneButtonIconFile?: File | null;
    }) => void;
}

export const FooterConfig = ({ id, initialConfig, onConfigChange }: FooterConfigProps) => {
    const baseId = id || 'footer-config';
    const defaultConfig: FooterConfigType = {
        showFooter: true,
        showDescription: true,
        descriptionText: 'Footer description or disclaimer text',
        showPreviousButton: true,
        previousButtonText: 'Previous',
        previousButtonShowIcon: false,
        previousButtonIconUrl: '',
        previousButtonShowTooltip: false,
        previousButtonTooltipText: '',
        showNextButton: true,
        nextButtonText: 'Next',
        nextButtonShowIcon: false,
        nextButtonIconUrl: '',
        nextButtonShowTooltip: false,
        nextButtonTooltipText: '',
        showDone: false,
        doneButtonText: 'Done',
        doneButtonShowIcon: false,
        doneButtonIconUrl: '',
        doneButtonShowTooltip: false,
        doneButtonTooltipText: ''
    };

    const [config, setConfig] = useState<FooterConfigType>({
        ...defaultConfig,
        ...initialConfig
    });

    // Use custom hooks for icon management
    const previousIconUpload = useIconUpload(
        initialConfig?.previousButtonIconUrl || '',
        (url) => updateConfig({ previousButtonIconUrl: url })
    );

    const nextIconUpload = useIconUpload(
        initialConfig?.nextButtonIconUrl || '',
        (url) => updateConfig({ nextButtonIconUrl: url })
    );

    const descriptionInput = useDebouncedInput(
        config.descriptionText,
        (value) => updateConfig({ descriptionText: value })
    );

    const previousButtonTextInput = useDebouncedInput(
        config.previousButtonText,
        (value) => updateConfig({ previousButtonText: value })
    );

    const nextButtonTextInput = useDebouncedInput(
        config.nextButtonText,
        (value) => updateConfig({ nextButtonText: value })
    );

    const previousTooltipInput = useDebouncedInput(
        config.previousButtonTooltipText || '',
        (value) => updateConfig({ previousButtonTooltipText: value })
    );

    const nextTooltipInput = useDebouncedInput(
        config.nextButtonTooltipText || '',
        (value) => updateConfig({ nextButtonTooltipText: value })
    );

    // Done button hooks
    const doneIconUpload = useIconUpload(
        initialConfig?.doneButtonIconUrl || '',
        (url) => updateConfig({ doneButtonIconUrl: url })
    );

    const doneButtonTextInput = useDebouncedInput(
        config.doneButtonText,
        (value) => updateConfig({ doneButtonText: value })
    );

    const doneTooltipInput = useDebouncedInput(
        config.doneButtonTooltipText || '',
        (value) => updateConfig({ doneButtonTooltipText: value })
    );

    const updateConfig = useCallback((updates: Partial<FooterConfigType>) => {
        setConfig(prevConfig => {
            const newConfig = { ...prevConfig, ...updates };
            setTimeout(() => {
                if (onConfigChange) {
                    onConfigChange({
                        ...newConfig,
                        previousButtonIconFile: previousIconUpload.file,
                        nextButtonIconFile: nextIconUpload.file,
                        doneButtonIconFile: doneIconUpload.file
                    });
                }
            }, 0);
            return newConfig;
        });
    }, [onConfigChange, previousIconUpload.file, nextIconUpload.file, doneIconUpload.file]);

    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pb-2 tw-my-4 tw-border-b tw-border-[#D1D1D6]">
                <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Footer</h2>
                <ToggleButton
                    id={`${baseId}-show-footer-toggle`}
                    name="show-footer"
                    value={config.showFooter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateConfig({ showFooter: e.target.checked })}
                />
            </div>

            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showDescription"
                        checked={config.showDescription}
                        onChange={(e) => updateConfig({ showDescription: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showDescription" className={styles.checkboxLabel}>{`Show 'Description'`}</label>
                </div>

                {config.showDescription && (
                    <div className={styles.nestedContainer}>
                        <div className={styles.nestedInputContainer}>
                            <label className={styles.inputLabel}>Description Text</label>
                            <input
                                type="text"
                                value={descriptionInput.localValue}
                                onChange={descriptionInput.handleChange}
                                className={styles.input}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Previous Button Configuration */}
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showPreviousButton"
                        checked={config.showPreviousButton}
                        onChange={(e) => updateConfig({ showPreviousButton: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showPreviousButton" className={styles.checkboxLabel}>{`Show 'Previous' Button`}</label>
                </div>

                {config.showPreviousButton && (
                    <div className={styles.nestedContainer}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showPreviousText"
                                checked={true}
                                className={styles.checkboxInput}
                                readOnly
                            />
                            <label htmlFor="showPreviousText" className={styles.checkboxLabelSmall}>Show Text</label>
                        </div>

                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Button Label</label>
                            <input
                                type="text"
                                value={previousButtonTextInput.localValue}
                                onChange={previousButtonTextInput.handleChange}
                                className={styles.input}
                            />
                        </div>

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showPreviousIcon"
                                checked={config.previousButtonShowIcon}
                                onChange={(e) => updateConfig({ previousButtonShowIcon: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showPreviousIcon" className={styles.checkboxLabelSmall}>Show Icon</label>
                        </div>

                        {config.previousButtonShowIcon && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Previous Button Icon</label>

                                {previousIconUpload.previewUrl && (
                                    <div className="tw-mb-2">
                                        <div className="tw-relative tw-inline-block">
                                            <img
                                                src={previousIconUpload.previewUrl}
                                                alt="Previous icon preview"
                                                className="tw-max-w-[40px] tw-max-h-[40px] tw-border tw-border-gray-300 tw-rounded tw-p-1"
                                            />
                                            <button
                                                onClick={previousIconUpload.handleRemove}
                                                className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-xs hover:tw-bg-red-600"
                                                title="Remove icon"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <input
                                    ref={previousIconUpload.inputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={previousIconUpload.handleUpload}
                                    className="tw-hidden"
                                    id="previous-icon-upload-input"
                                />

                                <button
                                    className={styles.uploadButton}
                                    onClick={() => previousIconUpload.inputRef.current?.click()}
                                >
                                    {previousIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon'}
                                </button>

                                {previousIconUpload.file && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                        Selected: {previousIconUpload.file.name}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showPreviousTooltip"
                                checked={config.previousButtonShowTooltip}
                                onChange={(e) => updateConfig({ previousButtonShowTooltip: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showPreviousTooltip" className={styles.checkboxLabelSmall}>Show tool tip on mouse hover</label>
                        </div>

                        {config.previousButtonShowTooltip && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Text for tool tip</label>
                                <input
                                    type="text"
                                    value={previousTooltipInput.localValue}
                                    onChange={previousTooltipInput.handleChange}
                                    placeholder="Enter"
                                    className={styles.input}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Next Button Configuration */}
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showNextButton"
                        checked={config.showNextButton}
                        onChange={(e) => updateConfig({ showNextButton: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showNextButton" className={styles.checkboxLabel}>{`Show 'Next' Button`}</label>
                </div>

                {config.showNextButton && (
                    <div className={styles.nestedContainer}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showNextText"
                                checked={true}
                                className={styles.checkboxInput}
                                readOnly
                            />
                            <label htmlFor="showNextText" className={styles.checkboxLabelSmall}>Show Text</label>
                        </div>

                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Button Label</label>
                            <input
                                type="text"
                                value={nextButtonTextInput.localValue}
                                onChange={nextButtonTextInput.handleChange}
                                className={styles.input}
                            />
                        </div>

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showNextIcon"
                                checked={config.nextButtonShowIcon}
                                onChange={(e) => updateConfig({ nextButtonShowIcon: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showNextIcon" className={styles.checkboxLabelSmall}>Show Icon</label>
                        </div>

                        {config.nextButtonShowIcon && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Next Button Icon</label>

                                {nextIconUpload.previewUrl && (
                                    <div className="tw-mb-2">
                                        <div className="tw-relative tw-inline-block">
                                            <img
                                                src={nextIconUpload.previewUrl}
                                                alt="Next icon preview"
                                                className="tw-max-w-[40px] tw-max-h-[40px] tw-border tw-border-gray-300 tw-rounded tw-p-1"
                                            />
                                            <button
                                                onClick={nextIconUpload.handleRemove}
                                                className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-xs hover:tw-bg-red-600"
                                                title="Remove icon"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <input
                                    ref={nextIconUpload.inputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={nextIconUpload.handleUpload}
                                    className="tw-hidden"
                                    id="next-icon-upload-input"
                                />

                                <button
                                    className={styles.uploadButton}
                                    onClick={() => nextIconUpload.inputRef.current?.click()}
                                >
                                    {nextIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon'}
                                </button>

                                {nextIconUpload.file && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                        Selected: {nextIconUpload.file.name}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showNextTooltip"
                                checked={config.nextButtonShowTooltip}
                                onChange={(e) => updateConfig({ nextButtonShowTooltip: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showNextTooltip" className={styles.checkboxLabelSmall}>Show tool tip on mouse hover</label>
                        </div>

                        {config.nextButtonShowTooltip && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Text for tool tip</label>
                                <input
                                    type="text"
                                    value={nextTooltipInput.localValue}
                                    onChange={nextTooltipInput.handleChange}
                                    placeholder="Enter"
                                    className={styles.input}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Done Button Configuration */}
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showDone"
                        checked={config.showDone}
                        onChange={(e) => updateConfig({ showDone: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showDone" className={styles.checkboxLabel}>{`Show 'Done' Button`}</label>
                </div>

                {config.showDone && (
                    <div className={styles.nestedContainer}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showDoneText"
                                checked={true}
                                className={styles.checkboxInput}
                                readOnly
                            />
                            <label htmlFor="showDoneText" className={styles.checkboxLabelSmall}>Show Text</label>
                        </div>

                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Button Label</label>
                            <input
                                type="text"
                                value={doneButtonTextInput.localValue}
                                onChange={doneButtonTextInput.handleChange}
                                className={styles.input}
                            />
                        </div>

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showDoneIcon"
                                checked={config.doneButtonShowIcon}
                                onChange={(e) => updateConfig({ doneButtonShowIcon: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showDoneIcon" className={styles.checkboxLabelSmall}>Show Icon</label>
                        </div>

                        {config.doneButtonShowIcon && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Done Button Icon</label>

                                {doneIconUpload.previewUrl && (
                                    <div className="tw-mb-2">
                                        <div className="tw-relative tw-inline-block">
                                            <img
                                                src={doneIconUpload.previewUrl}
                                                alt="Done icon preview"
                                                className="tw-max-w-[40px] tw-max-h-[40px] tw-border tw-border-gray-300 tw-rounded tw-p-1"
                                            />
                                            <button
                                                onClick={doneIconUpload.handleRemove}
                                                className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-xs hover:tw-bg-red-600"
                                                title="Remove icon"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <input
                                    ref={doneIconUpload.inputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={doneIconUpload.handleUpload}
                                    className="tw-hidden"
                                    id="done-icon-upload-input"
                                />

                                <button
                                    className={styles.uploadButton}
                                    onClick={() => doneIconUpload.inputRef.current?.click()}
                                >
                                    {doneIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon'}
                                </button>

                                {doneIconUpload.file && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                        Selected: {doneIconUpload.file.name}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                id="showDoneTooltip"
                                checked={config.doneButtonShowTooltip}
                                onChange={(e) => updateConfig({ doneButtonShowTooltip: e.target.checked })}
                                className={styles.checkboxInput}
                            />
                            <label htmlFor="showDoneTooltip" className={styles.checkboxLabelSmall}>Show tool tip on mouse hover</label>
                        </div>

                        {config.doneButtonShowTooltip && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Text for tool tip</label>
                                <input
                                    type="text"
                                    value={doneTooltipInput.localValue}
                                    onChange={doneTooltipInput.handleChange}
                                    placeholder="Enter"
                                    className={styles.input}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
